import type { ReactNode } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

export interface WorkspaceView {
  value: string
  label: string
  content: ReactNode
}

interface WorkspacePageProps {
  defaultView: string
  navigationLabel: string
  views: WorkspaceView[]
}

export function WorkspacePage({ defaultView, navigationLabel, views }: WorkspacePageProps) {
  const { pathname, search } = useLocation()
  const requestedView = new URLSearchParams(search).get('view')
  const defaultWorkspaceView = views.find((view) => view.value === defaultView)
  const activeView = views.find((view) => view.value === requestedView) ?? defaultWorkspaceView

  if (!activeView || !defaultWorkspaceView) {
    throw new Error('invalid_workspace_views')
  }

  function hrefForView(view: string) {
    const params = new URLSearchParams(search)
    if (view === defaultView) params.delete('view')
    else params.set('view', view)
    const nextSearch = params.toString()
    return `${pathname}${nextSearch ? `?${nextSearch}` : ''}`
  }

  return (
    <div className="space-y-6">
      <nav aria-label={navigationLabel} className="overflow-x-auto pb-1">
        <div className="inline-flex min-w-full rounded-lg border border-border bg-card p-1 sm:min-w-0">
          {views.map((view) => {
            const active = view.value === activeView.value
            return (
              <Link
                key={view.value}
                to={hrefForView(view.value)}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex h-10 min-w-max flex-1 items-center justify-center rounded-md px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  active
                    ? 'bg-secondary text-secondary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
                )}
              >
                {view.label}
              </Link>
            )
          })}
        </div>
      </nav>
      <div>{activeView.content}</div>
    </div>
  )
}

export function TranslatedWorkspacePage({
  defaultView,
  navigationLabelKey,
  views
}: {
  defaultView: string
  navigationLabelKey: string
  views: Array<Omit<WorkspaceView, 'label'> & { labelKey: string }>
}) {
  const { t } = useTranslation()
  return (
    <WorkspacePage
      defaultView={defaultView}
      navigationLabel={t(navigationLabelKey)}
      views={views.map(({ labelKey, ...view }) => ({ ...view, label: t(labelKey) }))}
    />
  )
}

export function LegacyWorkspaceRedirect({ to, view }: { to: string; view: string }) {
  const { search, hash } = useLocation()
  const params = new URLSearchParams(search)
  params.set('view', view)
  return <Navigate to={{ pathname: to, search: params.toString(), hash }} replace />
}
